import SectionContainer from '@/components/SectionContainer/SectionContainer';
import { useCarousel, useVisibilityObserver } from '@/modules/HomePageV2/hooks';
import { DoctorStory } from '@/modules/JobListing/utils/JobListingTypes';
import { pushGtmEvent } from '@/utils/gtm';
import { buildDoctorStoriesDescription } from '@/utils/jobListingHelper';
import cx from 'classnames';
import { useState } from 'react';

import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';

import styles from './DoctorStories.module.scss';
import { DoctorStoryCard } from './components/DoctorStoryCard/DoctorStoryCard';

const CARDS_PER_PAGE = 3;
const MOBILE_AUTO_ADVANCE_INTERVAL = 7000;

export interface DoctorStoriesProps {
  doctorStories: DoctorStory[];
  grade?: string;
  speciality?: string;
  location?: string;
}

// Desktop shows a 3-per-page grid with arrow pagination; mobile shows a single-card
// auto-advancing carousel with dot indicators (per Figma), reusing the same carousel
// hook as modules/HomePageV2/sections/Testimonials.
export const DoctorStories = ({
  doctorStories,
  grade,
  speciality,
  location,
}: DoctorStoriesProps) => {
  const [page, setPage] = useState(0);
  const { elementRef, isVisible } = useVisibilityObserver({ threshold: 0.1 });
  const {
    currentIndex,
    currentItem: currentMobileStory,
    isTransitioning,
    handleIndicatorClick,
  } = useCarousel<DoctorStory>({
    carouselData: doctorStories,
    isVisible,
    autoAdvanceInterval: MOBILE_AUTO_ADVANCE_INTERVAL,
  });

  if (!doctorStories.length) {
    return null;
  }

  const totalPages = Math.ceil(doctorStories.length / CARDS_PER_PAGE);
  // Clamps defensively in case doctorStories shrinks to fewer pages than the current one.
  const currentPage = Math.min(page, totalPages - 1);
  const visibleStories = doctorStories.slice(
    currentPage * CARDS_PER_PAGE,
    currentPage * CARDS_PER_PAGE + CARDS_PER_PAGE,
  );
  const description = buildDoctorStoriesDescription({ grade, speciality, location });

  return (
    <div className={styles.doctorStories} ref={elementRef}>
      <SectionContainer className={styles.doctorStoriesContainer}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <h3>Placed, paid and supported</h3>
            <p>{description}</p>
          </div>
          {totalPages > 1 && (
            <div className={styles.navButtons}>
              <button
                type="button"
                className={styles.navButton}
                aria-label="Previous doctor stories"
                disabled={currentPage === 0}
                onClick={() => {
                  setPage(current => Math.max(current - 1, 0));
                  pushGtmEvent({
                    event: 'testimonial_navigate',
                    element: 'Testimonial Navigation',
                    direction: 'prev',
                  });
                }}
              >
                <KeyboardArrowRight className={styles.prevIcon} />
              </button>
              <button
                type="button"
                className={styles.navButton}
                aria-label="Next doctor stories"
                disabled={currentPage >= totalPages - 1}
                onClick={() => {
                  setPage(current => Math.min(current + 1, totalPages - 1));
                  pushGtmEvent({
                    event: 'testimonial_navigate',
                    element: 'Testimonial Navigation',
                    direction: 'next',
                  });
                }}
              >
                <KeyboardArrowRight />
              </button>
            </div>
          )}
        </div>

        <div className={styles.desktopCardsRow}>
          {visibleStories.map(doctorStory => (
            <DoctorStoryCard key={doctorStory.id} doctorStory={doctorStory} />
          ))}
        </div>

        <div className={styles.mobileCarousel}>
          <div
            key={currentIndex}
            className={cx(styles.mobileCard, isTransitioning && styles.slideOut)}
          >
            <DoctorStoryCard doctorStory={currentMobileStory} />
          </div>
          <div className={styles.dots}>
            {doctorStories.map((doctorStory, index) => (
              <button
                key={doctorStory.id}
                type="button"
                className={cx(styles.dot, index === currentIndex && styles.dotActive)}
                aria-label={`View story ${index + 1} of ${doctorStories.length}`}
                onClick={() => {
                  handleIndicatorClick(index);
                  pushGtmEvent({
                    event: 'testimonial_dot_click',
                    element: 'Testimonial Dot',
                    dot_position: index + 1,
                  });
                }}
              />
            ))}
          </div>
        </div>
      </SectionContainer>
    </div>
  );
};

export default DoctorStories;
